/**
 * Detects line items that warrant a submit-time acknowledgement dialog.
 *
 * Two distinct kinds of mismatch are surfaced:
 *
 *  1. `off_contract` — the unit_price does not match any known pricing
 *     source (Special Pricing flat, tiered, Open Quote, agreement base
 *     discount, or Standard). Backend classifies these as
 *     `current_source.type === 'custom'`.
 *
 *  2. `po_waterfall_mismatch` — the unit_price WAS seeded from the
 *     pricing waterfall (matches a known source), but the PO that the
 *     LLM read disagreed. We still ship at the waterfall price, but we
 *     force the operator to acknowledge that the customer may expect the
 *     PO number. Backend signals this via
 *     `LineItemPricingContext.has_po_price_mismatch`.
 *
 * Used by the Submit-to-Sage flow to render a single blocking confirm
 * dialog covering both cases.
 */

import type {
  DetectedPricingSource,
  LineItemPricingContext,
  PricingSourceOption,
} from '../services/portalOrders';
import type { OrderLineItem } from '../types/orders';

export interface OffContractMismatch {
  kind: 'off_contract';
  lineItemId: string;
  itemCode: string;
  itemDescription: string | null;
  quantity: string | null;
  orderedPrice: number;
  /**
   * All known pricing sources for this item. Empty if no special pricing,
   * no quotes, and no standard price are available. Comes straight from
   * the backend's `available_sources` field.
   */
  availableSources: PricingSourceOption[];
}

export interface PoWaterfallMismatch {
  kind: 'po_waterfall_mismatch';
  lineItemId: string;
  itemCode: string;
  itemDescription: string | null;
  quantity: string | null;
  /** Price we'll actually ship at (waterfall-resolved). */
  orderedPrice: number;
  /** Price the LLM extracted from the PO. */
  poPrice: number;
  /** Backend-classified source of our shipping price (special/tiered/quote/standard). */
  currentSource: DetectedPricingSource | null;
  /** Human-readable source note when backend supplies one (e.g. "Quote #8431"). */
  priceSourceDetail: string | null;
}

/**
 * Human-readable label for a detected pricing source, specific enough that
 * the operator knows *which* contract the shipping price came from.
 *
 *   - special:  "Special Pricing"
 *   - tiered:   "Tiered Pricing (T3)"  (falls back to "Tiered Pricing" without label)
 *   - quote:    "Open Quote #8431"     (falls back to "Open Quote")
 *   - base_discount: "Special Pricing (base discount −10%)"
 *   - standard: "Standard Price"
 *   - custom/none/null: "Our Price"    (should not appear for PO-mismatch cards)
 */
export function sourceLabel(src: DetectedPricingSource | null): string {
  if (!src) return 'Our Price';
  switch (src.type) {
    case 'special':
      return 'Special Pricing';
    case 'tiered':
      return src.tier_label
        ? `Tiered Pricing (${src.tier_label})`
        : 'Tiered Pricing';
    case 'quote':
      return src.quote_source_id != null
        ? `Open Quote #${src.quote_source_id}`
        : 'Open Quote';
    case 'base_discount':
      return src.base_discount_pct != null
        ? `Special Pricing (base discount −${src.base_discount_pct}%)`
        : 'Special Pricing (base discount)';
    case 'promotion':
      return src.promotion_label ? `Promotion (${src.promotion_label})` : 'Promotion';
    case 'standard':
      return 'Standard Price';
    default:
      return 'Our Price';
  }
}

export type PriceMismatchItem = OffContractMismatch | PoWaterfallMismatch;

function parseNumericPrice(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseFloat(raw);
  return isNaN(n) ? null : n;
}

/**
 * Find all line items whose unit price does not match any known pricing
 * source — i.e. items the backend classified as `current_source.type === 'custom'`.
 *
 * Items with no unit price set, or for which the backend reports no
 * pricing context at all, are skipped (nothing to compare against).
 */
export function findPriceMismatches(
  lineItems: readonly OrderLineItem[],
  pricingItems: readonly LineItemPricingContext[] | null | undefined,
): OffContractMismatch[] {
  const pricingByCode = new Map<string, LineItemPricingContext>();
  for (const p of pricingItems ?? []) {
    pricingByCode.set(p.item_code, p);
  }

  const mismatches: OffContractMismatch[] = [];
  for (const li of lineItems) {
    const orderedPrice = parseNumericPrice(li.unit_price);
    if (orderedPrice === null) continue;

    const pricingItem = pricingByCode.get(li.item_code);
    // No pricing context for this item → can't classify; skip.
    if (!pricingItem || !pricingItem.current_source) continue;

    // Only 'custom' is off-contract. Everything else (special/tiered/
    // quote/base_discount/standard) is an on-contract match; 'none'
    // means no price.
    if (pricingItem.current_source.type !== 'custom') continue;

    mismatches.push({
      kind: 'off_contract',
      lineItemId: li.id,
      itemCode: li.item_code,
      itemDescription: li.item_description,
      quantity: li.quantity,
      orderedPrice,
      availableSources: pricingItem.available_sources,
    });
  }
  return mismatches;
}

/**
 * Find all line items where the LLM-extracted PO price disagrees with
 * the waterfall-resolved `unit_price` we'll actually ship at.
 *
 * Returns entries only when BOTH prices are present and differ, and
 * backend's `has_po_price_mismatch` flag confirms the mismatch.
 */
export function findPoWaterfallMismatches(
  lineItems: readonly OrderLineItem[],
  pricingItems: readonly LineItemPricingContext[] | null | undefined,
): PoWaterfallMismatch[] {
  const pricingByCode = new Map<string, LineItemPricingContext>();
  for (const p of pricingItems ?? []) {
    pricingByCode.set(p.item_code, p);
  }

  const mismatches: PoWaterfallMismatch[] = [];
  for (const li of lineItems) {
    const pricingItem = pricingByCode.get(li.item_code);
    if (!pricingItem || !pricingItem.has_po_price_mismatch) continue;

    const orderedPrice = parseNumericPrice(li.unit_price);
    const poPrice = parseNumericPrice(li.extracted_price);
    if (orderedPrice === null || poPrice === null) continue;
    // Defensive: backend already compared, but don't flag numerically-equal
    // values caused by formatting ("42.5" vs "42.50").
    if (orderedPrice === poPrice) continue;

    mismatches.push({
      kind: 'po_waterfall_mismatch',
      lineItemId: li.id,
      itemCode: li.item_code,
      itemDescription: li.item_description,
      quantity: li.quantity,
      orderedPrice,
      poPrice,
      currentSource: pricingItem.current_source,
      priceSourceDetail: pricingItem.price_source_detail,
    });
  }
  return mismatches;
}

/**
 * Convenience aggregator: returns both off-contract and PO-vs-waterfall
 * mismatches in a single array, off-contract first.
 */
export function findAllPriceMismatches(
  lineItems: readonly OrderLineItem[],
  pricingItems: readonly LineItemPricingContext[] | null | undefined,
): PriceMismatchItem[] {
  return [
    ...findPriceMismatches(lineItems, pricingItems),
    ...findPoWaterfallMismatches(lineItems, pricingItems),
  ];
}
